import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Container,
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Divider,
  createTheme,
  ThemeProvider,
  Chip,
  alpha,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import Header from './Header';

const LOCAL_SUCCESS_KEY = 'success-url';
const LOCAL_FAILURE_KEY = 'failure-url';
const theme = createTheme({
  palette: {
    primary: {
      main: '#2e7d32', // green
    },
    error: {
      main: '#d32f2f', // red
    },
  },
  typography: {
    fontFamily: 'Roboto, sans-serif',
    fontSize: 14,
  },
});

const SimulateUI = () => {
  const [successUrl, setSuccessUrl] = useState('');
  const [failureUrl, setFailureUrl] = useState('');
  const [storedSuccessUrl, setStoredSuccessUrl] = useState<string>('');
  const [storedFailureUrl, setStoredFailureUrl] = useState<string>('');

  useEffect(() => {
    const savedSuccess = localStorage.getItem(LOCAL_SUCCESS_KEY);
    const savedFailure = localStorage.getItem(LOCAL_FAILURE_KEY);
    if (savedSuccess) setStoredSuccessUrl(savedSuccess);
    if (savedFailure) setStoredFailureUrl(savedFailure);
  }, []);

  const handleSave = () => {
    if (successUrl.trim()) {
      localStorage.setItem(LOCAL_SUCCESS_KEY, successUrl.trim());
      setStoredSuccessUrl(successUrl.trim());
    }
    if (failureUrl.trim()) {
      localStorage.setItem(LOCAL_FAILURE_KEY, failureUrl.trim());
      setStoredFailureUrl(failureUrl.trim());
    }
  };

  const handleSuccess = () => {
    window.location.href = storedSuccessUrl;
  };

  const handleFailure = () => {
    window.location.href = storedFailureUrl;
  };

  const handleReset = () => {
    localStorage.removeItem(LOCAL_SUCCESS_KEY);
    localStorage.removeItem(LOCAL_FAILURE_KEY);
    setStoredSuccessUrl('');
    setStoredFailureUrl('');
    setSuccessUrl('');
    setFailureUrl('');
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="sm">
        <Header>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            minHeight="30vh"
            textAlign="center"
            sx={{paddingBottom:10}}
            gap={3}
          >

            {storedSuccessUrl && storedFailureUrl ? (
              <>
                <Card sx={{ width: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Saved URLs
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                      <CheckCircleIcon color="success" />
                      <Typography variant="body1">{storedSuccessUrl}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CancelIcon color="error" />
                      <Typography variant="body1">{storedFailureUrl}</Typography>
                    </Stack>
                  </CardContent>
                </Card>
                <Stack direction="row" spacing={2}>
                  <Button variant="contained" color="success" onClick={handleSuccess}>
                    Success
                  </Button>
                  <Button variant="contained" color="error" onClick={handleFailure}>
                    Failure
                  </Button>
                  <Button variant="outlined" color="primary" onClick={handleReset} startIcon={<EditIcon />}>
                    Edit URLs
                  </Button>
                </Stack>
              </>
            ) : (
              <>
                <TextField
                  label="Success URL"
                  variant="outlined"
                  fullWidth
                  value={successUrl}
                  onChange={(e) => setSuccessUrl(e.target.value)}
                />
                <TextField
                  label="Failure URL"
                  variant="outlined"
                  fullWidth
                  value={failureUrl}
                  onChange={(e) => setFailureUrl(e.target.value)}
                />
                <Button variant="contained" onClick={handleSave} sx={{ mt: 2 }}>
                  Save URLs
                </Button>
              </>
            )}

            {/* Give Back Section */}
            <Card
              sx={{
                mt: 4,
                mb: 2,
                background: `linear-gradient(135deg, ${alpha('#FFD700', 0.1)}, ${alpha(theme.palette.primary.main, 0.1)})`,
                border: `1px solid ${alpha('#FFD700', 0.3)}`,
                borderRadius: '15px',
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: `linear-gradient(90deg, #FFD700, ${theme.palette.primary.main}, #FFD700)`,
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      background: `linear-gradient(45deg, #FFD700, ${theme.palette.primary.main})`,
                      backgroundClip: 'text',
                      textFillColor: 'transparent',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 1,
                    }}
                  >
                    💛 Donate Back – Not Just Money
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary',
                      mb: 1,
                    }}
                  >
                    If Release Pilot boosted your productivity by 0.1%—give back through <strong>impact, not money</strong> 🌟
                  </Typography>
                </Box>

                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: 1,
                    mb: 2,
                  }}
                >
                  {['🤝 Help others', '👥 Mentor fellow developer', '🚀 Share opportunities', '💡 Contribute skills', '❤️ Spread kindness'].map((item, index) => (
                    <Chip
                      key={index}
                      label={item}
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: alpha('#FFD700', 0.5),
                        color: 'text.primary',
                        '&:hover': {
                          backgroundColor: alpha('#FFD700', 0.1),
                          transform: 'scale(1.02)',
                        },
                      }}
                    />
                  ))}
                </Box>

                <Typography 
                  variant="body2" 
                  sx={{ 
                    textAlign: 'center',
                    color: 'text.secondary',
                    fontStyle: 'italic',
                  }}
                >
                  Every small action matters. Let your gratitude become someone else's breakthrough. 🙌
                </Typography>
              </CardContent>
            </Card>

            {/* Crafted with Love */}
            <Typography 
              variant="body1" 
              sx={{ 
                textAlign: 'center',
                color: 'text.secondary',
                mt: 3,
                mb: 2,
                fontSize: '1.1rem',
              }}
            >
              Crafted with ❤️ in India
            </Typography>
          </Box>
        </Header>
      </Container>
    </ThemeProvider>
  );
};
export default SimulateUI;
